import React, { useCallback, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { FormProvider, useForm } from 'react-hook-form'
import { Box, Dialog, DialogContent, useMediaQuery, useTheme } from '@mui/material'
import Grid from '@mui/material/Grid'
import { get, noop } from 'lodash'
import HHDialogTitle from '../../../common/HHDialogTitle'
import T from 'T'
import AddressDetails from 'components/customer-details/content/common/AddressDetails'
import AddressFormFields from 'components/customer-details/content/billing-location-details/AddressFormFields'
import HHDialogActions from 'components/common/HHDialogActions'
import CancelButton from 'components/buttons/CancelButton'
import SaveButton from 'components/buttons/SaveButton'
import { selectAddress } from 'data/address/addressSelectors'
import { useLazyGetOverridingBeneficialOwnerContactQuery, useUpdateBeneficialOwnerContactMutation } from 'api/contact/beneficialOwnersCrud'
import { combineAddressInfo, splitAddressLine1 } from 'utils/helper'
import { handleError } from 'utils/error'

const defaultValues = {
  addressName: '',
  city: '',
  country: '',
  line1: '',
  state: '',
  unitNumber: '',
  zipCode: '',
}

const addressObjToOptionFormat = addressObj => {
  const { line1, city, state, zipCode, country, latitude, longitude } = addressObj
  const splittedLine1 = splitAddressLine1(line1 || '')
  const streetNumber = splittedLine1.number
  const { streetName } = splittedLine1
  return {
    ...addressObj,
    city,
    country,
    stateShortCode: state,
    streetName,
    streetNumber,
    zipCode,
    center: {
      longitude,
      latitude,
    },
    text: combineAddressInfo(addressObj),
  }
}

const EditBeneficialOwnerLocationDialog = ({ open = false, accountId, id, onClose = noop, onSave = noop }) => {
  const [getBeneficialOwnerDetails, { data: existingBeneficialOwnerData }] = useLazyGetOverridingBeneficialOwnerContactQuery()
  const methods = useForm({ defaultValues })
  const { reset, handleSubmit, watch } = methods
  const watchAllFields = watch()
  const address = useMemo(() => get(existingBeneficialOwnerData, 'address', {}), [existingBeneficialOwnerData])
  const firstName = useMemo(() => get(existingBeneficialOwnerData, 'firstName', ''), [existingBeneficialOwnerData])
  const lastName = useMemo(() => get(existingBeneficialOwnerData, 'lastName', ''), [existingBeneficialOwnerData])
  const fullName = useMemo(() => [firstName, lastName].filter(Boolean).join(' '), [firstName, lastName])
  const { city, state, latitude, longitude, line1, zipCode, unitNumber } = useMemo(() => selectAddress(address), [address])
  const theme = useTheme()
  const isTabletOrMobile = useMediaQuery(theme.breakpoints.down('lg'))
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'))
  const [updateBeneficialOwnerDetails, { isLoading: isUpdating }] = useUpdateBeneficialOwnerContactMutation()

  const handleAddressChange = () => {}

  const handleSave = useCallback(
    data => {
      // eslint-disable-next-line no-shadow
      const { line1, unitNumber, city, state, country, zipCode, addressName } = data

      const address = {
        attention: addressName,
        line1,
        unitNumber,
        city,
        state,
        country,
        zipCode,
      }
      updateBeneficialOwnerDetails({ accountId, id, ...existingBeneficialOwnerData, address: address })
        .unwrap()
        .then(() => {
          onSave()
        })
        .catch(handleError)
    },
    [existingBeneficialOwnerData]
  )

  useEffect(() => {
    if (open && id && accountId) {
      getBeneficialOwnerDetails({ accountId, id })
    }
  }, [open])

  useEffect(() => {
    if (open && address) {
      reset({
        address: addressObjToOptionFormat(address),
        city,
        latitude,
        line1,
        longitude,
        state,
        unitNumber,
        zipCode,
      })
    } else {
      reset(defaultValues)
    }
  }, [open, address])

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <HHDialogTitle sx={{ px: 3, pt: 3 }} closeIconSx={{ top: 16 }} onClose={onClose} title={T.EDIT_BENEFICIAL_OWNER_ADDRESS} />
      <DialogContent sx={{ px: 3 }}>
        <FormProvider {...methods}>
          <Grid container spacing={2}>
            <Grid container size={{ xs: 12, sm: 12, md: 5, lg: 6, xl: 6 }}>
              <Grid container size={12}>
                {isDesktop && (
                  <Grid size={{ xs: 12, sm: 12, md: 'grow', lg: 'grow', xl: 'grow' }}>
                    <AddressDetails
                      spacing={2}
                      sx={{ px: 2, pt: isTabletOrMobile ? 4 : 3, pb: 6 }}
                      address={{
                        ...watchAllFields,
                        addressName: fullName,
                      }}
                      line1Label={T.BENEFICIAL_OWNER_ADDRESS}
                      showAddressName
                      addressNameLabel={T.BENEFICIAL_OWNER}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 7, lg: 6, xl: 5 }}>
              <Box sx={{ mt: 2 }}>
                <AddressFormFields onAddressChange={handleAddressChange} />
              </Box>
            </Grid>
          </Grid>
        </FormProvider>
      </DialogContent>
      <HHDialogActions>
        <CancelButton onClick={onClose} />
        <SaveButton loading={isUpdating} disabled={isUpdating} onClick={handleSubmit(handleSave)} />
      </HHDialogActions>
    </Dialog>
  )
}

EditBeneficialOwnerLocationDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  id: PropTypes.string,
  accountId: PropTypes.string,
}

export default EditBeneficialOwnerLocationDialog
